/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * "Chủ thể & liên hệ" (v2.1): the registrant in full (and, for every type but an individual, its
 * legal representative in full, like an individual), then the domain's own four contacts —
 * registrant, admin, tech, billing — each with its data, its ID at the registry, where it came
 * from (copied at registration, synced from the subject, edited for this domain only) and the
 * fields that now differ from the subject, highlighted. Editing a contact changes this domain
 * only; "Đồng bộ từ chủ thể" is staff's choice. Changing the registrant is its own procedure.
 */

import { ArrowRight, History, RefreshCw, ShieldCheck, UserRound } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { api, type DomainDetail, type SubjectDetail } from '../../../api';
import type { SubjectProfile } from '../../../api/areas/accounts';
import type { ContactData, ContactField, ContactRole, DomainContact, DomainContacts } from '../../../api/areas/core';
import { Badge } from '../../../components/Badge';
import { Callout, DataTable, Section } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { RevealIdentifier } from '../../../components/RevealIdentifier';
import { EmptyState, Loading } from '../../../components/States';
import { addressLine } from '../../../lib/address';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { ReasonAction } from '../../operator/shared';
import { ContactRef, CustomerRef, RoleBadge, SubjectRef, useVocab } from '../refs';
import { pinnedVerification, syncGroups, syncOutcomes } from './contactSync';
import { EditContactDialog } from './DomainContactEdit';
import { DomainExtraFieldsSection } from '../ExtraFields';
import { domainTabUrl } from './moved';
import { EditAction, type DomainTabProps } from './shared';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

const ROLES: readonly ContactRole[] = ['REGISTRANT', 'ADMIN', 'TECH', 'BILLING'];

export type FactRow = readonly [ReactNode, ReactNode, (boolean | string)?];

/**
 * A label / value grid. A row whose third element is set is highlighted: `true` says it differs
 * from the registrant now, a string says what else differs.
 */
export function Facts({ rows, columns = 2 }: { rows: readonly (FactRow | null | false)[]; columns?: 1 | 2 }) {
  const { t } = useTranslation('domainPage');
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-4', columns === 2 && 'sm:grid-cols-2')}>
      {rows
        .filter((r): r is FactRow => Boolean(r))
        .map(([k, v, changed], i) => (
          <div key={i} className={cn('min-w-0', changed && '-mx-2 rounded-md bg-warning-soft px-2 py-1')} data-changed={changed ? 'true' : undefined}>
            <dt className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              {k}
              {changed ? <span className="font-semibold text-warning">{typeof changed === 'string' ? changed : t('contacts.differs')}</span> : null}
            </dt>
            <dd className="mt-1 text-[13px] font-medium break-words">{v ?? <span className="text-muted-foreground">—</span>}</dd>
          </div>
        ))}
    </dl>
  );
}

const text = (v: string | null | undefined): ReactNode => (v && v.trim() ? v : null);

function addressOf(x: { vn_address?: { full_address: string } | null; address?: { street: string[]; city: string; province: string | null; postal_code: string | null; country_code: string } | null }): string | null {
  if (x.vn_address?.full_address) return x.address?.postal_code ? `${x.vn_address.full_address} (${x.address.postal_code})` : x.vn_address.full_address;
  return x.address ? addressLine(x.address) : null;
}

/** Identity numbers with their audited reveal (the tax code rule of each subject type under them). */
function Identifiers({ profile }: { profile: SubjectProfile }) {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  if (!profile.identifiers.length) return null;
  return (
    <span className="grid gap-1.5">
      {profile.identifiers.map((i) => (
        <span key={i.id ?? `${i.type}-${i.hint}`} className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{tr(`subject.identifierTypes.${i.type}`, { defaultValue: i.type })}</span>
          {i.id ? <RevealIdentifier identifier={{ id: i.id, type: i.type, country: i.country, hint: i.hint }} /> : <code className="text-xs">{i.hint ?? '—'}</code>}
        </span>
      ))}
    </span>
  );
}

function BirthDate({ subjectId, profile }: { subjectId: string; profile: SubjectProfile }) {
  const { t } = useTranslation('domainPage');
  if (!profile.has_birth_date) return <span className="text-muted-foreground">{t('subject.birthMissing')}</span>;
  return (
    <RevealIdentifier
      identifier={{ id: subjectId, type: 'BIRTH_DATE', country: null, hint: '••/••/••••' }}
      revealer={(reason, opts) => api.revealSubjectBirthDate(subjectId, reason, opts)}
      formatValue={(v) => formatDate(v)}
    />
  );
}

/** A person's facts (an individual registrant, or a representative "with every detail an individual has"). */
export function PersonFacts({ subjectId, p, representative }: { subjectId: string; p: SubjectProfile; representative?: boolean }) {
  const { t } = useTranslation('domainPage');
  const { t: td } = useTranslation('domains');
  const tr = td as unknown as Lookup;
  return (
    <Facts
      rows={[
        [t('subject.fullName'), p.legal_name],
        representative ? [t('subject.position'), text(p.position)] : null,
        [t('subject.birthDate'), <BirthDate key="b" subjectId={subjectId} profile={p} />],
        [t('subject.gender'), p.gender ? tr(`subject.genders.${p.gender.toUpperCase()}`, { defaultValue: p.gender }) : null],
        [p.subject_type === 'INDIVIDUAL' && !representative ? t('subject.idAndTax') : t('subject.idNumber'), <Identifiers key="i" profile={p} />],
        [t('subject.nationality'), text(p.nationality)],
        [t('subject.phone'), text(p.phone)],
        [t('subject.email'), text(p.email)],
        [t('subject.address'), addressOf(p)],
      ]}
    />
  );
}

/** An organisation / household / public body / foreign / other subject's own facts. */
export function OrgFacts({ p }: { p: SubjectProfile }) {
  const { t } = useTranslation('domainPage');
  return (
    <Facts
      rows={[
        [t('subject.legalName'), p.legal_name],
        [t('subject.tradeName'), text(p.trade_name)],
        [p.subject_type === 'HOUSEHOLD_BUSINESS' ? t('subject.householdTax') : t('subject.taxCode'), <Identifiers key="i" profile={p} />],
        [t('subject.founded'), p.establish_date ? formatDate(p.establish_date) : null],
        [t('subject.phone'), text(p.phone)],
        [t('subject.email'), text(p.email)],
        [t('subject.fax'), text(p.fax)],
        [t('subject.country'), text(p.country_code)],
        [t('subject.headOffice'), addressOf(p)],
      ]}
    />
  );
}

function Verification({ s, pinnedVersion }: { s: SubjectDetail; pinnedVersion: string | null | undefined }) {
  const { t } = useTranslation('domainPage');
  const vocab = useVocab();
  const v = pinnedVerification(s.verifications, pinnedVersion);
  if (!v) return <Badge tone="warn">{t('subject.notVerified')}</Badge>;
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge tone={v.status === 'VERIFIED' ? 'ok' : v.status === 'PENDING' || v.status === 'IN_PROGRESS' ? 'pending' : 'warn'}>{vocab('check', v.status)}</Badge>
      <span className="text-xs text-muted-foreground">
        {vocab('method', v.method)}
        {v.verified_at ? ` · ${formatDate(v.verified_at)}` : ''}
      </span>
    </span>
  );
}

/** "Người đại diện theo pháp luật" (every type but an individual): the linked individual subject, in full. */
export function Representative({ s }: { s: SubjectDetail }) {
  const { t } = useTranslation('domainPage');
  const link = s.profile?.relationships?.find((r) => r.type === 'LEGAL_REPRESENTATIVE' && !r.valid_until);
  const rep = useApi((signal) => (link ? api.getSubject(link.related_subject_id, { signal }) : Promise.resolve(null)), [link?.related_subject_id]);
  const household = s.subject_type === 'HOUSEHOLD_BUSINESS';
  return (
    <Section
      title={household ? t('subject.owner') : t('subject.representative')}
      description={t('subject.representativeHint')}
      actions={
        link ? (
          <Button asChild variant="outline" size="sm">
            <Link to={`/subjects/${encodeURIComponent(link.related_subject_id)}`}>
              {t('subject.openRepresentative')} <ArrowRight />
            </Link>
          </Button>
        ) : null
      }
    >
      {!link ? (
        <Callout tone="warn" title={t('subject.noRepresentative')}>
          {t('subject.noRepresentativeBody')}{' '}
          <Link to={`/subjects/${encodeURIComponent(s.id)}?edit=1`} className="font-medium text-primary hover:underline">
            {t('subject.editSubject')}
          </Link>
        </Callout>
      ) : rep.status === 'loading' ? (
        <Loading rows={2} />
      ) : rep.status === 'error' ? (
        <DataError error={rep.error} onRetry={rep.reload} title={t('subject.representativeFailed')} />
      ) : rep.data?.profile ? (
        <>
          <p className="mb-4 text-xs text-muted-foreground">{t('subject.since', { date: formatDate(link.valid_from) })}</p>
          <PersonFacts subjectId={rep.data.id} p={rep.data.profile} representative />
        </>
      ) : null}
    </Section>
  );
}

function RegistrantPanel({ detail, s }: { detail: DomainDetail; s: SubjectDetail }) {
  const { t } = useTranslation('domainPage');
  const { t: td } = useTranslation('domains');
  const tr = td as unknown as Lookup;
  const p = s.profile;
  const pinned = detail.dossier?.subject_version_number ?? null;
  const current = s.versions?.find((v) => v.id === s.current_version_id)?.version_number ?? null;
  return (
    <Section
      title={t('subject.title')}
      description={t('subject.titleHint')}
      actions={
        <Button asChild variant="outline" size="sm">
          <Link to={`/subjects/${encodeURIComponent(s.id)}?edit=1`}>{t('subject.editSubject')}</Link>
        </Button>
      }
    >
      <div className="mb-5 flex items-center gap-4 border-b pb-5">
        <span className="flex size-13 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
          <UserRound className="size-6" aria-hidden="true" />
        </span>
        <div className="min-w-0 space-y-2">
          <h2 className="text-lg font-semibold break-words">{p?.legal_name ?? detail.domain.subject_display_name}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="info" icon={false}>
              {tr(`subject.types.${s.subject_type}`, { defaultValue: s.subject_type })}
            </Badge>
            {pinned ? (
              <Badge tone={current && pinned !== current ? 'warn' : 'neutral'} icon={false}>
                {current && pinned !== current ? t('subject.pinnedOld', { pinned, current }) : t('subject.pinned', { n: pinned })}
              </Badge>
            ) : null}
            <Verification s={s} pinnedVersion={detail.dossier?.subject_version_id} />
          </div>
        </div>
      </div>
      {p ? s.subject_type === 'INDIVIDUAL' ? <PersonFacts subjectId={s.id} p={p} /> : <OrgFacts p={p} /> : <p className="text-sm text-muted-foreground">{t('subject.noProfile')}</p>}
    </Section>
  );
}

/** Which rows of a contact a changed field marks. */
const CHANGED: Record<string, ContactField> = { name: 'legal_name', organization: 'legal_name', email: 'email', phone: 'phone', fax: 'fax', address: 'address', ids: 'identifier', position: 'details', nationality: 'details' };

function ContactCard({ c, registry, sync, onEdit }: { c: DomainContact | undefined; registry: DomainContacts; sync: boolean; onEdit: (c: DomainContact) => void }) {
  const { t } = useTranslation('domainPage');
  const { t: td } = useTranslation('domains');
  const tr = td as unknown as Lookup;
  if (!c) return null;
  const changed = new Set(c.changed_fields ?? []);
  const x: ContactData | null | undefined = c.details;
  const mark = (row: string) => changed.has(CHANGED[row] as ContactField);
  const origin = c.origin ?? 'CLONED';
  const drift = changed.size > 0;
  return (
    <article className={cn('flex min-w-0 flex-col rounded-xl border bg-card', drift && 'border-warning/50')} data-contact-role={c.role} data-drift={drift ? 'true' : undefined}>
      <header className="flex flex-wrap items-start justify-between gap-2 border-b px-4 py-3">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <RoleBadge role={c.role} />
            <span className="text-[13.5px] font-semibold break-words">{x?.name ?? c.display_name ?? '—'}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone={origin === 'EDITED' ? 'pending' : 'neutral'} icon={false}>
              {t(`contacts.origins.${origin}`)}
            </Badge>
            {sync ? (
              <Badge tone="pending">{t('contacts.syncing')}</Badge>
            ) : drift ? (
              <Badge tone="warn">{t('contacts.drift', { count: changed.size })}</Badge>
            ) : (
              <Badge tone="ok">{t('contacts.matches')}</Badge>
            )}
          </div>
        </div>
        {c.role !== 'REGISTRANT' && !c.valid_until ? (
          <Button type="button" variant="outline" size="sm" onClick={() => onEdit(c)}>
            {t('contacts.edit')}
          </Button>
        ) : null}
      </header>
      <div className="space-y-4 px-4 py-4">
        <Facts
          rows={[
            [t('contacts.registryId'), c.handle ? <ContactRef key="h" registry={registry.registry_code} handle={c.handle} /> : <span key="h" className="text-xs text-muted-foreground">{t('contacts.noHandle')}</span>],
            x?.organization ? [t('contacts.organization'), x.organization, mark('organization')] : null,
            [t('contacts.name'), x?.name ?? c.display_name, mark('name')],
            x?.position ? [t('contacts.position'), x.position, mark('position')] : null,
            [t('contacts.email'), text(x?.email), mark('email')],
            [t('contacts.phone'), text(x?.phone), mark('phone')],
            x?.fax ? [t('contacts.fax'), x.fax, mark('fax')] : null,
            [t('contacts.address'), x ? addressOf(x) : null, mark('address')],
            [t('contacts.ids'), x?.identifiers.length ? x.identifiers.map((i) => `${tr(`subject.identifierTypes.${i.type}`, { defaultValue: i.type })} ${i.hint ?? ''}`.trim()).join(' · ') : null, mark('ids')],
            x?.nationality ? [t('contacts.nationality'), x.nationality, mark('nationality')] : null,
          ]}
        />
        {x === null ? <p className="text-xs text-muted-foreground">{t('contacts.noDetails')}</p> : null}
      </div>
      <footer className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 border-t px-4 py-2.5 text-xs text-muted-foreground">
        <span>
          {t('contacts.fromSubject')}{' '}
          <SubjectRef id={c.subject_id} name={c.display_name} />
          {c.subject_version_number ? ` · ${t('contacts.version', { n: c.subject_version_number })}` : ''}
          {c.subject_version_is_current === false ? ` · ${t('contacts.notCurrent')}` : ''}
        </span>
        <span>{t('contacts.since', { date: formatDateTime(c.valid_from) })}</span>
        {c.cloned_at && origin !== 'CLONED' ? <span>{t('contacts.copiedAt', { date: formatDate(c.cloned_at) })}</span> : null}
      </footer>
    </article>
  );
}

function ContactsPanel({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const { t: td } = useTranslation('domains');
  const vocab = useVocab();
  const d = detail.domain;
  const contacts = useApi((signal) => api.getDomainContacts(d.id, { signal }), [d.id, d.version]);
  const [editing, setEditing] = useState<DomainContact | null>(null);
  const [past, setPast] = useState(false);
  if (contacts.status === 'loading') return <Loading rows={3} />;
  if (contacts.status === 'error') return <DataError error={contacts.error} onRetry={contacts.reload} title={td('contacts.loadFailed')} />;
  const data = contacts.data;
  const current = data.items.filter((c) => !c.valid_until);
  const ended = data.items.filter((c) => c.valid_until);
  const drifted = current.filter((c) => (c.changed_fields ?? []).length > 0);
  // One sync per subject the drifted contacts were copied from (a sync changes only its subject's).
  const groups = syncGroups(current);
  return (
    <div id="contacts" className="scroll-mt-20 space-y-4">
      <Section
        title={t('contacts.title')}
        description={t('contacts.hint')}
        actions={
          <>
            <EditAction detail={detail} action="registryContacts" label={t('contacts.atRegistry')} />
            {groups.length
              ? groups.map((g) => {
                  const roles = g.roles.map((r) => vocab('role', r)).join(', ');
                  const name = g.name ?? t('contacts.theirSubject');
                  return (
                    <ReasonAction
                      key={g.subjectId}
                      label={groups.length > 1 ? t('contacts.syncFrom', { name: g.name ?? roles }) : t('contacts.syncFromSubject')}
                      icon={RefreshCw}
                      // One contact update of a domain runs at a time.
                      disabled={Boolean(data.sync_running)}
                      title={t('contacts.syncTitle', { domain: d.unicode_name })}
                      description={t('contacts.syncDescription', { count: g.roles.length, roles, name })}
                      run={(reason, opts) => api.syncSubjectToDomains(g.subjectId, { domain_ids: [d.id], reason }, opts)}
                      done={(r) => syncOutcomes(r.results).map((o) => t(`contacts.syncResult.${o}`)).join(' ')}
                      onChanged={contacts.reload}
                    />
                  );
                })
              : null}
          </>
        }
      >
        {data.supports_contact_objects === false ? <Callout className="mb-4">{td('contacts.thin')}</Callout> : null}
        {drifted.length ? (
          <Callout tone="warn" className="mb-4" title={t('contacts.driftTitle', { count: drifted.length })}>
            {t('contacts.driftBody')}
          </Callout>
        ) : null}
        {current.length ? (
          <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
            {ROLES.map((role) => (
              <ContactCard key={role} c={current.find((c) => c.role === role)} registry={data} sync={Boolean(data.sync_running)} onEdit={setEditing} />
            ))}
          </div>
        ) : (
          <EmptyState title={td('contacts.empty')}>{td('contacts.emptyBody')}</EmptyState>
        )}
        {ended.length ? (
          <div className="mt-4">
            <Button type="button" variant="ghost" size="sm" onClick={() => setPast((v) => !v)} aria-expanded={past}>
              <History /> {past ? t('contacts.hidePast') : t('contacts.showPast', { count: ended.length })}
            </Button>
            {past ? (
              <div className="mt-2 -mx-5">
                <DataTable
                  columns={[
                    { key: 'role', header: td('contacts.role'), cell: (c) => <RoleBadge role={c.role} /> },
                    { key: 'contact', header: td('contacts.contact'), cell: (c) => <ContactRef registry={data.registry_code} handle={c.handle} /> },
                    { key: 'subject', header: td('contacts.subject'), cell: (c) => <SubjectRef id={c.subject_id} name={c.display_name} type={c.subject_type} /> },
                    { key: 'origin', header: t('contacts.source'), cell: (c) => t(`contacts.origins.${c.origin ?? 'CLONED'}`) },
                    { key: 'period', header: td('contacts.period'), className: 'whitespace-nowrap', cell: (c) => `${formatDate(c.valid_from)} – ${formatDate(c.valid_until)}` },
                  ]}
                  rows={ended}
                  rowKey={(c) => c.id}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </Section>
      {editing ? <EditContactDialog detail={detail} role={editing.role} current={editing.subject_id} onClose={() => setEditing(null)} onChanged={() => contacts.reload()} /> : null}
    </div>
  );
}

/** "Tài khoản & phạm vi ảnh hưởng": whose account pays, and what a change here touches. */
function ScopePanel({ detail, s }: { detail: DomainDetail; s: SubjectDetail | null }) {
  const { t } = useTranslation('domainPage');
  const d = detail.domain;
  const others = (s?.domains ?? []).filter((x) => x.id !== d.id);
  return (
    <Section title={t('scope.title')} description={t('scope.hint')}>
      <Facts
        rows={[
          [t('scope.account'), <CustomerRef key="c" id={d.customer_id} name={d.customer_name} />],
          [t('scope.domain'), <span key="n" className="font-mono">{d.ascii_name}</span>],
          [t('scope.change'), t('scope.changeValue')],
          [
            t('scope.others'),
            s ? (
              others.length ? (
                <span key="o" className="flex flex-wrap gap-x-2 gap-y-1">
                  {others.slice(0, 6).map((x) => (
                    <Link key={x.id} to={domainTabUrl(x.ascii_name, 'subject')} className="text-primary hover:underline">
                      {x.unicode_name}
                    </Link>
                  ))}
                  {others.length > 6 ? <span>+{others.length - 6}</span> : null}
                </span>
              ) : (
                t('scope.none')
              )
            ) : null,
          ],
        ]}
      />
      <Callout className="mt-5" title={t('scope.ownershipTitle')}>
        {t('scope.ownershipBody')}{' '}
        <Link to={domainTabUrl(d.ascii_name, 'transfer')} className="font-medium text-primary hover:underline">
          {t('scope.ownershipLink')}
        </Link>
      </Callout>
    </Section>
  );
}

export function SubjectContactsTab({ detail }: DomainTabProps) {
  const { t } = useTranslation('domainPage');
  const { t: td } = useTranslation('domains');
  const id = detail.domain.current_subject_id;
  const subject = useApi((signal) => (id ? api.getSubject(id, { signal }) : Promise.resolve(null)), [id]);
  const s = subject.status === 'success' ? subject.data : null;
  return (
    <>
      {!id ? (
        <EmptyState title={td('subjectTab.none')} icon={ShieldCheck}>
          {td('subjectTab.noneBody')}
        </EmptyState>
      ) : subject.status === 'loading' ? (
        <Loading rows={3} />
      ) : subject.status === 'error' ? (
        <DataError error={subject.error} onRetry={subject.reload} title={t('subject.failed')} />
      ) : s ? (
        <>
          <RegistrantPanel detail={detail} s={s} />
          {s.subject_type !== 'INDIVIDUAL' ? <Representative s={s} /> : null}
        </>
      ) : null}
      <ContactsPanel detail={detail} />
      <DomainExtraFieldsSection detail={detail} />
      <ScopePanel detail={detail} s={s} />
    </>
  );
}
